import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { createInkMaterial } from '../render/InkMaterial';
import { createSeededRandom } from '../utils/random';

export const PLANET_RADIUS = 36;
export const WATER_RADIUS = PLANET_RADIUS - 0.35;

export type PlanetPalette = {
  grass: number;
  grassDark: number;
  dirt: number;
  rock: number;
  sand: number;
  water: number;
  trunk: number;
  leaves: number[];
  roof: number[];
  wall: number;
  stone: number;
  cobble: number;
  cobbleDark: number;
};

/** A paved area (cobbles) or a road band painted onto the terrain. */
export type Paving = { dir: THREE.Vector3; radius: number } | { from: THREE.Vector3; to: THREE.Vector3; width: number };

/** A point of interest pinned to the planet surface. */
export type Landmark = { name: string; dir: THREE.Vector3; radius: number };

const UP = new THREE.Vector3(0, 1, 0);

/** Direction that is (dx, dz) world units across the surface from `dir`, in a fixed tangent frame. */
export function offsetDir(dir: THREE.Vector3, dx: number, dz: number): THREE.Vector3 {
  const up = dir.clone().normalize();
  const ref = Math.abs(up.y) < 0.9 ? UP : new THREE.Vector3(1, 0, 0);
  const t1 = new THREE.Vector3().crossVectors(ref, up).normalize();
  const t2 = new THREE.Vector3().crossVectors(up, t1);
  return up.multiplyScalar(PLANET_RADIUS).addScaledVector(t1, dx).addScaledVector(t2, dz).normalize();
}

// Cheap smooth 3D noise for terrain (gradient-free value noise, fbm).
function hash3(x: number, y: number, z: number): number {
  const s = Math.sin(x * 127.1 + y * 311.7 + z * 74.7) * 43758.5453;
  return s - Math.floor(s);
}
function vnoise(x: number, y: number, z: number): number {
  const xi = Math.floor(x), yi = Math.floor(y), zi = Math.floor(z);
  const xf = x - xi, yf = y - yi, zf = z - zi;
  const u = xf * xf * (3 - 2 * xf), v = yf * yf * (3 - 2 * yf), w = zf * zf * (3 - 2 * zf);
  const l = (a: number, b: number, t: number) => a + (b - a) * t;
  const c = (dx: number, dy: number, dz: number) => hash3(xi + dx, yi + dy, zi + dz);
  return l(
    l(l(c(0, 0, 0), c(1, 0, 0), u), l(c(0, 1, 0), c(1, 1, 0), u), v),
    l(l(c(0, 0, 1), c(1, 0, 1), u), l(c(0, 1, 1), c(1, 1, 1), u), v),
    w,
  );
}
function fbm(p: THREE.Vector3, oct = 4): number {
  let a = 0.5, f = 1, s = 0;
  for (let i = 0; i < oct; i++) {
    s += a * (vnoise(p.x * f, p.y * f, p.z * f) - 0.5);
    f *= 2.03;
    a *= 0.5;
  }
  return s;
}

/** Lets a world split the planet into regions (e.g. a modern half). */
export type PlanetHooks = {
  /** 0..1: how much to flatten terrain at this direction (1 = dead flat city ground). */
  flat?: (d: THREE.Vector3) => number;
  /** Override the ground colour; return null to keep the default. */
  paint?: (d: THREE.Vector3, defaultColor: number) => number | null;
  /** True where no trees or rocks should be scattered. */
  blocked?: (d: THREE.Vector3) => boolean;
  /** Extra height (world units) added to the terrain: mountains, lake beds. */
  lift?: (d: THREE.Vector3) => number;
};

export class Planet {
  readonly group = new THREE.Group();
  collider!: THREE.Mesh;
  readonly landmarks: Landmark[];
  private readonly flatten: { dir: THREE.Vector3; radius: number }[];
  private readonly colliderParts: THREE.BufferGeometry[] = [];

  private readonly paving: Paving[];
  private readonly trunkMat: THREE.ShaderMaterial;
  private readonly leafMats: THREE.ShaderMaterial[];

  constructor(private readonly palette: PlanetPalette, landmarks: Landmark[], seed = 7, paving: Paving[] = [], private readonly hooks: PlanetHooks = {}) {
    this.paving = paving;
    this.landmarks = landmarks;
    this.trunkMat = createInkMaterial({ color: palette.trunk, lineBreak: 0.2 });
    this.leafMats = palette.leaves.map((c) => createInkMaterial({ color: c, lineBreak: 0.45, lineScale: 4, band: [0.15, 0.28] }));
    // Landmarks sit on flattened pads so buildings and camps aren't on a slope.
    this.flatten = landmarks.map((l) => ({ dir: l.dir.clone().normalize(), radius: l.radius }));
    const rand = createSeededRandom(seed);

    const terrain = this.buildTerrain();
    this.group.add(terrain);
    this.colliderParts.push(terrain.geometry.clone().deleteAttribute('color'));

    const water = new THREE.Mesh(
      new THREE.IcosahedronGeometry(WATER_RADIUS, 24),
      createInkMaterial({ color: palette.water, band: [0.0, 0.05], lineBreak: 0.0, tone: 0.03 }),
    );
    water.receiveShadow = true;
    this.group.add(water);

    this.scatterProps(rand);
  }

  /** Call once everything that should block movement has been added. */
  buildCollider(): THREE.Mesh {
    const merged = mergeGeometries(this.colliderParts.map((g) => (g.index ? g.toNonIndexed() : g)).map(stripToPosition));
    this.collider = new THREE.Mesh(merged);
    this.collider.visible = false;
    return this.collider;
  }

  /** Terrain height (distance from centre) along a unit direction. */
  heightAt(dir: THREE.Vector3): number {
    let h = fbm(dir.clone().multiplyScalar(2.2)) * 3.2 + fbm(dir.clone().multiplyScalar(7)) * 0.55;
    for (const f of this.flatten) {
      const a = dir.angleTo(f.dir) * PLANET_RADIUS;
      const k = THREE.MathUtils.smoothstep(a, f.radius, f.radius * 1.8);
      h = THREE.MathUtils.lerp(0.25, h, k);
    }
    const flat = this.hooks.flat?.(dir) ?? 0;
    if (flat > 0) h = THREE.MathUtils.lerp(h, 0.25, flat);
    h += this.hooks.lift?.(dir) ?? 0;
    return PLANET_RADIUS + h;
  }

  surfacePoint(dir: THREE.Vector3, lift = 0): THREE.Vector3 {
    const d = dir.clone().normalize();
    return d.multiplyScalar(this.heightAt(d) + lift);
  }

  private buildTerrain(): THREE.Mesh {
    const geo = new THREE.IcosahedronGeometry(1, 74);
    const pos = geo.attributes.position as THREE.BufferAttribute;
    const d = new THREE.Vector3();
    for (let i = 0; i < pos.count; i++) {
      d.fromBufferAttribute(pos, i).normalize();
      d.multiplyScalar(this.heightAt(d));
      pos.setXYZ(i, d.x, d.y, d.z);
    }
    geo.computeVertexNormals();

    const p = this.palette;
    const colors: number[] = [];
    const n = new THREE.Vector3();
    const c = new THREE.Color();
    const nrm = geo.attributes.normal as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) {
      d.fromBufferAttribute(pos, i);
      n.fromBufferAttribute(nrm, i);
      const r = d.length();
      const slope = 1 - n.dot(d.clone().normalize());
      const patch = vnoise(d.x * 0.35, d.y * 0.35, d.z * 0.35);
      const pave = this.pavedAt(d.clone().normalize());
      if (pave > 0) {
        // Irregular cobbles: a cell pattern in two stone tones, dirt in the cracks near the edges.
        const cell = vnoise(d.x * 0.95, d.y * 0.95, d.z * 0.95);
        c.setHex(pave < 0.35 ? p.dirt : cell > 0.5 ? p.cobble : p.cobbleDark);
      } else if (r < WATER_RADIUS + 0.35) c.setHex(p.sand);
      else if (slope > 0.22) c.setHex(p.rock);
      else if (slope > 0.12) c.setHex(p.dirt);
      else c.setHex(patch > 0.55 ? p.grassDark : p.grass);
      if (this.hooks.paint) {
        const over = this.hooks.paint(d.clone().normalize(), c.getHex());
        if (over !== null) c.setHex(over);
      }
      colors.push(c.r, c.g, c.b);
    }
    geo.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    const mesh = new THREE.Mesh(geo, createInkMaterial({ vertexColors: true, lineBreak: 0.38, lineScale: 2.5 }));
    mesh.receiveShadow = true;
    mesh.castShadow = true;
    return mesh;
  }

  /** Direction that is (dx, dz) world units across the surface from `dir`. */
  offset(dir: THREE.Vector3, dx: number, dz: number): THREE.Vector3 {
    return offsetDir(dir, dx, dz);
  }

  /** Pin an object to the surface, upright, with a yaw. Returns it. */
  pin(obj: THREE.Object3D, dir: THREE.Vector3, yaw = 0, sink = 0.05): THREE.Object3D {
    const d = dir.clone().normalize();
    obj.position.copy(this.surfacePoint(d, -sink));
    obj.quaternion.setFromUnitVectors(UP, d);
    obj.rotateY(yaw);
    this.group.add(obj);
    return obj;
  }

  /** Add a simple collision proxy (already in world space via obj matrix). */
  addCollider(geo: THREE.BufferGeometry, obj: THREE.Object3D): void {
    obj.updateMatrixWorld(true);
    this.colliderParts.push(geo.clone().applyMatrix4(obj.matrixWorld));
  }

  private nearLandmark(d: THREE.Vector3, pad = 1.4): boolean {
    return this.flatten.some((f) => d.angleTo(f.dir) * PLANET_RADIUS < f.radius * pad) || this.pavedAt(d) > 0 || !!this.hooks.blocked?.(d);
  }

  /** 0 = not paved, up to 1 deep inside a paved area. */
  pavedAt(d: THREE.Vector3): number {
    let best = 0;
    for (const pv of this.paving) {
      let dist: number, half: number;
      if ('radius' in pv) {
        dist = d.angleTo(pv.dir) * PLANET_RADIUS;
        half = pv.radius;
      } else {
        // Distance to the great-circle arc between from and to.
        const n = new THREE.Vector3().crossVectors(pv.from, pv.to).normalize();
        const off = Math.asin(THREE.MathUtils.clamp(d.dot(n), -1, 1)) * PLANET_RADIUS;
        const proj = d.clone().addScaledVector(n, -d.dot(n)).normalize();
        const along = proj.angleTo(pv.from) + proj.angleTo(pv.to) - pv.from.angleTo(pv.to);
        dist = along > 0.01 ? 1e9 : Math.abs(off);
        half = pv.width / 2;
      }
      if (dist < half) best = Math.max(best, Math.min(1, (half - dist) / 1.2));
    }
    return best;
  }

  /** Local tangent-plane coordinates of `d` around `centre` (inverse of offset()). */
  toLocal(centre: THREE.Vector3, d: THREE.Vector3): THREE.Vector2 {
    const up = centre.clone().normalize();
    const ref = Math.abs(up.y) < 0.9 ? UP : new THREE.Vector3(1, 0, 0);
    const t1 = new THREE.Vector3().crossVectors(ref, up).normalize();
    const t2 = new THREE.Vector3().crossVectors(up, t1);
    const n = d.clone().normalize();
    const p = n.multiplyScalar(PLANET_RADIUS / Math.max(0.05, n.dot(up))).sub(up.clone().multiplyScalar(PLANET_RADIUS));
    return new THREE.Vector2(p.dot(t1), p.dot(t2));
  }

  private scatterProps(rand: () => number): void {
    const p = this.palette;
    const randDir = () => {
      const z = rand() * 2 - 1, a = rand() * Math.PI * 2, s = Math.sqrt(1 - z * z);
      return new THREE.Vector3(s * Math.cos(a), z, s * Math.sin(a));
    };
    const trunkMat = this.trunkMat;
    const leafMats = this.leafMats;
    const rockMat = createInkMaterial({ color: p.stone, lineBreak: 0.25 });

    const placed: THREE.Vector3[] = [];
    let trees = 0;
    for (let i = 0; i < 1500 && trees < 140; i++) {
      const d = randDir();
      if (this.heightAt(d) < WATER_RADIUS + 0.6) continue;
      if (this.nearLandmark(d)) continue;
      if (placed.some((q) => q.angleTo(d) * PLANET_RADIUS < 2.2)) continue;
      // Forests cluster: keep trees where a low-frequency noise is high.
      if (vnoise(d.x * 3 + 9, d.y * 3, d.z * 3) < 0.42) continue;
      placed.push(d);
      const s = 0.8 + rand() * 0.7;
      const tree = rand() < 0.5 ? this.pine(s, trunkMat, leafMats[0]) : this.roundTree(s, trunkMat, leafMats[1 + Math.floor(rand() * (leafMats.length - 1))]);
      this.pin(tree, d, rand() * Math.PI * 2);
      this.addCollider(new THREE.CylinderGeometry(0.22 * s, 0.22 * s, 3, 6).translate(0, 1.5, 0), tree);
      trees++;
    }
    for (let i = 0; i < 70; i++) {
      const d = randDir();
      if (this.nearLandmark(d, 1.1)) continue;
      const s = 0.3 + rand() * 0.9;
      const rock = new THREE.Mesh(new THREE.DodecahedronGeometry(s, 0), rockMat);
      rock.scale.set(1, 0.7, 1.1);
      rock.castShadow = rock.receiveShadow = true;
      this.pin(rock, d, rand() * 6, s * 0.25);
      if (s > 0.6) this.addCollider(new THREE.SphereGeometry(s * 0.8, 6, 4), rock);
    }
  }

  /** Plant one tree (with a collider) at a direction. */
  addTree(d: THREE.Vector3, s: number, pine: boolean, colorIndex = 1, yaw = 0): THREE.Object3D {
    const tree = pine ? this.pine(s, this.trunkMat, this.leafMats[0]) : this.roundTree(s, this.trunkMat, this.leafMats[Math.min(this.leafMats.length - 1, Math.max(1, colorIndex))]);
    this.pin(tree, d, yaw);
    this.addCollider(new THREE.CylinderGeometry(0.22 * s, 0.22 * s, 3, 6).translate(0, 1.5, 0), tree);
    return tree;
  }

  private pine(s: number, trunk: THREE.Material, leaf: THREE.Material): THREE.Group {
    const g = new THREE.Group();
    const t = new THREE.Mesh(new THREE.CylinderGeometry(0.1 * s, 0.16 * s, 1.1 * s, 6).translate(0, 0.55 * s, 0), trunk);
    g.add(t);
    for (let i = 0; i < 3; i++) {
      const cone = new THREE.Mesh(new THREE.ConeGeometry((1.05 - i * 0.25) * s, 1.3 * s, 8).translate(0, (1.3 + i * 0.7) * s, 0), leaf);
      g.add(cone);
    }
    g.traverse((o) => { o.castShadow = true; o.receiveShadow = true; });
    return g;
  }

  private roundTree(s: number, trunk: THREE.Material, leaf: THREE.Material): THREE.Group {
    const g = new THREE.Group();
    g.add(new THREE.Mesh(new THREE.CylinderGeometry(0.11 * s, 0.17 * s, 1.5 * s, 6).translate(0, 0.75 * s, 0), trunk));
    const blobs: [number, number, number, number][] = [[0, 2.0, 0, 0.95], [0.55, 1.65, 0.2, 0.6], [-0.45, 1.75, -0.3, 0.65], [0.1, 2.55, -0.1, 0.55]];
    for (const [x, y, z, r] of blobs) g.add(new THREE.Mesh(new THREE.IcosahedronGeometry(r * s, 1).translate(x * s, y * s, z * s), leaf));
    g.traverse((o) => { o.castShadow = true; o.receiveShadow = true; });
    return g;
  }
}

function stripToPosition(g: THREE.BufferGeometry): THREE.BufferGeometry {
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', g.getAttribute('position'));
  return out;
}
